import type { GenerationPhase, SlidePlan } from "../hooks/types";
import styles from "./CreateSlide.module.css";

interface GenerationProgressProps {
  readonly topic: string;
  readonly current: number;
  readonly total: number;
  readonly plans: SlidePlan[];
  readonly phase: GenerationPhase;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onRestart: () => void;
}

function statusDetail(plan: SlidePlan): string {
  if (plan.detail) return plan.detail;
  if (plan.status === "waiting") return "Waiting…";
  if (plan.status === "done") return "Added to deck";
  return "";
}

export function GenerationProgress({
  topic,
  current,
  total,
  plans,
  phase,
  error,
  onCancel,
  onRestart,
}: GenerationProgressProps) {
  const running = phase === "outlining" || phase === "generating";
  let heading: string;
  if (phase === "done") {
    heading = "Deck created";
  } else if (phase === "cancelled") {
    heading = "Generation cancelled";
  } else if (phase === "error") {
    // Don't keep saying "Creating…" once generation has failed.
    heading = "Generation failed";
  } else {
    heading = `Creating: ${topic || "your deck"}`;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.genHead}>
        <div className={styles.genHeadText}>
          <span className={styles.genTitle}>{heading}</span>
          <span className={styles.genCounter}>
            {phase === "done" ? `${total} of ${total}` : `${current} of ${total}`}
          </span>
        </div>
        {running ? (
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </button>
        ) : (
          <button type="button" className={styles.cancelBtn} onClick={onRestart}>
            Create another
          </button>
        )}
      </div>

      <div className={styles.genList}>
        {plans.map((plan) => {
          const active = plan.status === "picking" || plan.status === "generating";
          const busy = active;
          return (
            <div
              key={plan.index}
              className={`${styles.genItem} ${active ? styles.genItemActive : ""}`}
            >
              {(() => {
                if (busy) return <span className={styles.genSpinner} aria-hidden="true" />;
                if (plan.status === "done") {
                  return (
                    <span className={styles.genCheck} aria-hidden="true">
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3.5 8.5l3 3 6-6.5" />
                      </svg>
                    </span>
                  );
                }
                return <span className={styles.genDot} aria-hidden="true" />;
              })()}
              <span className={styles.genItemText}>
                <span className={styles.genItemTitle}>
                  Slide {plan.index}: {plan.title}
                </span>
                <span className={styles.genItemDetail}>{statusDetail(plan)}</span>
              </span>
              {plan.status === "picking" && <span className={styles.genBadge}>Picking</span>}
            </div>
          );
        })}
      </div>

      {phase === "done" &&
        (() => {
          const added = plans.filter((p) => p.status === "done").length;
          const failed = plans.filter((p) => p.status === "error");
          if (failed.length === 0) {
            // Edge case: outline returned no slides — don't show a green
            // "0 slides added" success, which reads as a contradiction.
            if (added === 0) {
              return <div className={styles.doneBanner}>No slides were generated.</div>;
            }
            return (
              <div className={`${styles.doneBanner} ${styles.doneBannerOk}`}>
                ✅ {added} slide{added === 1 ? "" : "s"} added to your presentation.
              </div>
            );
          }
          return (
            <div className={styles.doneBanner}>
              ⚠️ {added} of {total} slides added — {failed.length} failed.
              <br />
              <span className={styles.error}>{failed[0]?.detail}</span>
            </div>
          );
        })()}
      {phase === "cancelled" && (
        <div className={styles.doneBanner}>Generation was cancelled. Slides created so far remain in the deck.</div>
      )}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}
